<template>
    <v-container fluid v-if="driverShift && order">
        <v-card tile elevation="0">
            <v-toolbar color="upt" dark dense class="text-uppercase">
                Station readings: {{ station.Name }} - #{{
                    order.station.StationNumber
                }}
            </v-toolbar>
            <v-expansion-panels tile>
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        Station map
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <site-map
                            :stationId="station.StationID"
                            height="300px"
                        ></site-map>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
            <v-alert
                dense
                type="warning"
                dismissible
                v-if="station.recentReadings.length == 0"
            >
                No readings found for this station.
            </v-alert>
            <!--<trailer-compartments
                :trailer="driverShift.trailer"
                :orderDetails="order.order_details"
                :products="products"
            />-->
            <template v-if="station.recentReadings.length > 0">
                <v-progress-linear
                    :value="(totalUsage / trailerLimit) * 100"
                    :height="25"
                    dark
                >
                    {{ totalUsage }} / {{ trailerLimit }}
                </v-progress-linear>
                <v-data-table
                    :headers="recentReadingsHeaders"
                    :items="station.recentReadings"
                    hide-default-footer
                    dense
                >
                    <template v-slot:item.Product="{ item }">
                        {{ item.Product }}
                        <span
                            v-if="getProduct(item.ProductID)?.MaxCap == 1"
                            class="text-uppercase"
                            style="color: red; font-weight: bold; font-size: 0.8rem;"
                        >
                            DIESEL
                        </span>
                    </template>
                    <template v-slot:item.Volume="{ item }">
                        {{ formatNumber(item.Volume) }}
                    </template>
                    <template v-slot:item.ullage="{ item }">
                        <span>
                            {{
                                formatNumber(
                                    getUllage(item.ProductID, item.Volume)
                                )
                            }}
                        </span>
                    </template>
                    <template v-slot:item.ullage_at_eta="{ item }">
                        <span>
                            {{
                                formatNumber(
                                    getUllage(item.ProductID, item.volumeAtETA)
                                )
                            }}
                        </span>
                    </template>
                    <template v-slot:item.safe_pump="{ item }">
                        <span>
                            {{
                                formatNumber(
                                    getStationProduct(item.ProductID)
                                        .total_safe_pump
                                )
                            }}
                        </span>
                    </template>
                    <template v-slot:item.safe_fill="{ item }">
                        <span>
                            {{
                                formatNumber(
                                    getStationProduct(item.ProductID)
                                        ?.total_safe_fill
                                )
                            }}
                        </span>
                    </template>
                    <template v-slot:item.runout="{ item }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <span
                                    v-bind="attrs"
                                    v-on="on"
                                    class="ttDisplay"
                                >
                                    {{
                                        timeUntil(
                                            getStationProduct(item.ProductID)
                                                .safe_runout_at,
                                            'HH'
                                        )
                                    }}
                                    Hrs
                                </span>
                            </template>
                            {{
                                formatHumanReadableDateTime(
                                    getStationProduct(item.ProductID)
                                        .safe_runout_at
                                )
                            }}
                        </v-tooltip>
                    </template>
                    <template v-slot:item.new_runout="{ item }">
                        <v-tooltip top v-if="item.newRunout">
                            <template v-slot:activator="{ on, attrs }">
                                <span
                                    v-bind="attrs"
                                    v-on="on"
                                    :class="{
                                        'upt--text font-weight-bold':
                                            newClosestRunout?.product_id ==
                                            item.ProductID
                                    }"
                                    class="ttDisplay"
                                >
                                    {{
                                        timeUntil(
                                            item.newRunout.runoutDateTime,
                                            'HH'
                                        )
                                    }}
                                    Hrs
                                </span>
                            </template>
                            {{
                                formatHumanReadableDateTime(
                                    item.newRunout.runoutDateTime
                                )
                            }}
                        </v-tooltip>
                    </template>
                    <template #header.ullage_at_nro="{ item }">
                        <span v-show="newClosestRunout">
                            Ullage at
                            {{
                                newClosestRunout?.product_id
                                    ? getProduct(newClosestRunout.product_id)
                                          .Product
                                    : 'N/A'
                            }}
                            NRO
                        </span>
                    </template>
                    <template v-slot:item.ullage_at_nro="{ item }">
                        <span v-if="item.volumeAtClosestRunout">
                            {{
                                formatNumber(
                                    getUllage(
                                        item.ProductID,
                                        item.volumeAtClosestRunout
                                    )
                                )
                            }}
                        </span>
                    </template>
                    <template v-slot:item.product_quantity="{ item }">
                        <v-text-field
                            v-model="
                                productOrderDetails[item.ProductID]
                                    .ProductQuantity
                            "
                            :suffix="
                                'AQ: ' +
                                    parseString(
                                        productOrderDetails[item.ProductID]
                                            .AdjustedQuantity
                                    ) +
                                    ' Lts.'
                            "
                            type="number"
                            dense
                            @change="
                                handleProductQuantityChange(item.ProductID)
                            "
                            :error-messages="getErrorMessages(item.ProductID)"
                        />
                    </template>
                    <!-- You can access other properties from order_detail here -->
                    <template v-slot:item.product_code="{ item }">
                        <v-text-field
                            v-model="
                                productOrderDetails[item.ProductID].ProductCode
                            "
                            dense
                            hide-details
                            @change="updateOrderDetail(item.ProductID)"
                        />
                    </template>
                </v-data-table>
                <v-divider class="mt-2"></v-divider>
                <v-toolbar flat dense>
                    <v-btn
                        color="teal"
                        dark
                        text
                        @click="autoAssignQuantities"
                        class="my-2 mx-1"
                    >
                        <v-icon left>mdi-creation</v-icon>
                        Auto Assign Quantities
                    </v-btn>
                    <v-btn text @click="getNewRunouts" class="my-2 mx-1">
                        <v-icon left>mdi-refresh</v-icon>
                        Get NEW Runout
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="success" class="my-2 mx-1" @click="saveQuantity">
                        <v-icon left>mdi-check</v-icon>
                        Save
                    </v-btn>
                </v-toolbar>
            </template>
            <div v-else>
                <v-data-table
                    :headers="orderDetailsHeaders"
                    :items="order.order_details"
                    hide-default-footer
                    dense
                >
                    <template v-slot:item.ProductType="{ item }">
                        <v-autocomplete
                            v-model="item.ProductType"
                            :items="products"
                            item-text="Product"
                            item-value="ProductID"
                            dense
                            hide-details
                        >
                        </v-autocomplete>
                    </template>
                    <template v-slot:item.ProductCode="{ item }">
                        <v-text-field
                            v-model="item.ProductCode"
                            type="number"
                            dense
                            hide-details
                        ></v-text-field>
                    </template>
                    <template v-slot:item.ProductQuantity="{ item }">
                        <v-text-field
                            v-model="item.ProductQuantity"
                            type="number"
                            dense
                            hide-details
                            @change="
                                handleProductQuantityChange(item.ProductType)
                            "
                            :error-messages="getErrorMessages(item.ProductType)"
                        ></v-text-field>
                    </template>
                    <template v-slot:item.action="{ item }">
                        <v-btn
                            small
                            color="red"
                            plain
                            @click="deleteProduct(item)"
                        >
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </template>
                </v-data-table>

                <v-toolbar class="mt-2" flat dense>
                    <v-btn
                        color="green"
                        dark
                        @click="addNewOrderDetail()"
                        :disabled="totalUsage >= trailerLimit"
                    >
                        <v-icon>mdi-plus</v-icon>
                        Add new product
                    </v-btn>
                </v-toolbar>
            </div>
        </v-card>
        <v-card tile elevation="0">
            <v-toolbar color="upt" dark dense class="text-uppercase">
                Order Information: #{{ order.OrderID }} (Shift #{{
                    driverShift.ShiftDetail
                }})
            </v-toolbar>
            <v-card-text>
                <v-form>
                    <v-row>
                        <v-col>
                            <v-text-field
                                label="Terminal"
                                v-model="order.terminal.TerminalName"
                                dense
                                hide-details
                                outlined
                                disabled
                            ></v-text-field>
                        </v-col>
                        <v-col>
                            <v-text-field
                                label="Station"
                                v-model="station.Name"
                                dense
                                hide-details
                                outlined
                                disabled
                            ></v-text-field>
                        </v-col>
                        <v-divider vertical></v-divider>
                        <v-col>
                            <v-text-field
                                label="Customer"
                                v-model="order.customer.Name"
                                dense
                                hide-details
                                outlined
                                disabled
                            ></v-text-field>
                        </v-col>
                        <v-col>
                            <v-text-field
                                v-if="order.supplier"
                                label="Supplier"
                                v-model="order.supplier.Name"
                                value="N/A"
                                dense
                                hide-details
                                outlined
                                disabled
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-divider class="my-2"></v-divider>
                    <v-row>
                        <v-col>
                            <v-text-field
                                label="ETA"
                                v-model="order.ETATime"
                                dense
                                hide-details
                                outlined
                                disabled
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import TrailerCompartments from './TrailerCompartments.vue';
import SiteMap from '../../../Global/SiteMap.vue';

import { dateUtils } from '../../../mixins/dateUtils';
import { dataUtils } from '../../../mixins/dataUtils';
import { apiService } from '../../../services/apiService';
import axios from 'axios';
export default {
    name: 'AssignOrderToDriverShift',
    components: {
        TrailerCompartments,
        SiteMap
    },
    props: ['driverShift', 'order', 'products', 'station'],
    mixins: [dateUtils, dataUtils],
    data() {
        return {
            recentReadingsHeaders: [
                { text: 'Product', value: 'Product' },
                { text: 'Volume', value: 'Volume' },
                { text: 'Ullage', value: 'ullage' },
                { text: 'Ullage at ETA', value: 'ullage_at_eta' },
                { text: 'Safe Pump', value: 'safe_pump' },
                { text: 'Safe Fill', value: 'safe_fill', divider: true },
                { text: 'RO', value: 'runout' },
                { text: 'NRO', value: 'new_runout' },
                {
                    text: 'Ullage at NRO',
                    value: 'ullage_at_nro',
                    divider: true
                },
                { text: 'Product Code', value: 'product_code' },
                { text: 'Quantity', value: 'product_quantity' }
            ],
            stationProductsHeaders: [
                { text: 'Product', value: 'Product' },
                { text: 'Volume', value: 'Volume' },
                { text: 'Safe Fill', value: 'SafeFill' },
                { text: 'Runout', value: 'Runout' }
            ],
            orderDetailsHeaders: [
                { text: 'Product', value: 'ProductType' },
                { text: 'Product Code', value: 'ProductCode' },
                { text: 'Quantity', value: 'ProductQuantity' },
                { text: 'Actions', value: 'action' }
            ],
            productOrderDetails: {},
            limits: {
                6: 60000,
                5: 58000,
                4: 55000,
                3: 30000, // 3 axles
                1: 20000 // 1 axle
            },
            errorMessages: {},
            totalUsage: 0,
            trailerLimit: 0,
            newClosestRunout: null,
            compartments: []
        };
    },
    watch: {
        order: {
            handler() {
                this.initializeProductOrderDetails();
            },
            immediate: true
        },
        driverShift: {
            handler() {
                this.trailerLimit = this.limits[
                    this.driverShift.trailer.NumAxles
                ];
                console.log('Updated Trailer limit: ', this.trailerLimit);
            },
            immediate: true
        }
    },
    methods: {
        saveQuantity: function(){
            var str = this.$store;
            console.log(this.productOrderDetails);
             var parms = {
                 Quantity: this.productOrderDetails
             }
             axios 
             .post('order-detail/update/quantitys',parms , {
                 headers: {
                     Authorization:
                         'Bearer ' + this.$store.state.token
                 }
             })
            //   eslint-disable-next-line no-unused-vars
             .then(response => {
                 console.log(response.data);
                 str.state.isLoading = false;
             })
             .catch(function(error) {
                 console.log(error);
                 str.dispatch('setSnackbar', {
                     color: 'error',
                     text:
                         'Something is wrong. Please contact the administrator.'
                 });
                 str.state.isLoading = false;
             });
        },
        initializeProductOrderDetails() {
            this.productOrderDetails = {};

            // Populate from existing order_details
            if (this.order && this.order.order_details) {
                this.order.order_details.forEach(detail => {
                    this.productOrderDetails[detail.ProductType] = detail;
                    this.totalUsage += detail.ProductQuantity;
                    console.log('Total Usage: ', this.totalUsage);
                });
            }

            // Ensure entries exist for all products in recentReadings
            if (this.station && this.station.recentReadings) {
                this.station.recentReadings.forEach(reading => {
                    const productId = reading.ProductID;
                    if (!this.productOrderDetails[productId]) {
                        const newOrderDetail = this.createOrderDetail(
                            productId,
                            null
                        );
                        this.productOrderDetails[productId] = newOrderDetail;
                    }
                });
            }
        },
        getOrderDetail(productId) {
            return this.productOrderDetails[productId];
        },

        createOrderDetail(productId, quantity) {
            const newOrderDetail = {
                OrderProdID: Date.now(), // Temporary ID, adjust as needed
                OrderID: this.order.OrderID,
                ProductType: productId,
                ProductQuantity: quantity,
                DispatchedQuantity: null,
                DeliveredQuantity: null,
                ProductCode: null
            };
            // Add to productOrderDetails for reactivity
            this.$set(this.productOrderDetails, productId, newOrderDetail);
            return newOrderDetail;
        },
        handleProductQuantityChange(productId) {
            console.info('handleProductQuantityChange: ', productId);
            if (!this.productOrderDetails[productId]) {
                const newOrderDetail = this.createOrderDetail(productId, null);
                this.productOrderDetails[productId] = newOrderDetail;
                this.order.order_details.push(newOrderDetail);
            }
            // Proceed with recalculations
            this.updateOrderDetail(productId);

            // Update the order details with adjusted quantities
            this.order.order_details.forEach(detail => {
                const originalDetail = this.productOrderDetails[
                    detail.ProductType
                ];
                originalDetail.ProductQuantity = detail.ProductQuantity;
            });
        },
        updateOrderDetail(productId) {
            const orderDetail = this.productOrderDetails[productId];
            const existingIndex = this.order.order_details.findIndex(
                detail => detail.ProductType == productId
            );

            if (existingIndex === -1) {
                // Add to order.order_details
                this.order.order_details.push(orderDetail);
            }
            // Else, the order detail is already in order.order_details, no action needed
        },
        getErrorMessages(productId) {
            return this.errorMessages[productId] || '';
        },
        getStationProduct(productId) {
            return this.station.products.find(
                product => product.product_id == productId
            );
        },
        getProduct(productId) {
            const product = this.products.find(
                product => product.ProductID == productId
            );
            return product ? product : null;
        },
        getReadingsData(productId) {
            return this.station.recentReadings.find(
                product => product.ProductID == productId
            );
        },
        async getNewRunouts() {
            try {
                // Create an array to hold all the promises
                const runoutPromises = [];

                // Loop through each product and prepare the requests
                for (const productId in this.productOrderDetails) {
                    const orderDetail = this.productOrderDetails[productId];
                    const productReadings = this.getReadingsData(productId);

                    // Calculate total volume for the request
                    const totalVolume = orderDetail.ProductQuantity
                        ? orderDetail.ProductQuantity +
                          productReadings.volumeAtETA
                        : productReadings.Volume;
                    const params = {
                        station: this.order.Station,
                        product: productId,
                        datetime: this.order.ETATime,
                        volume: totalVolume
                    };

                    // Push each API request as a promise into the array
                    runoutPromises.push(apiService.getRunoutSimulation(params));
                }

                // Wait for all runout requests to complete
                const newRunouts = await Promise.all(runoutPromises);
                let closestRunout = null;

                // Process the new runouts and update `recentReadings`
                for (let i = 0; i < newRunouts.length; i++) {
                    const newRunout = newRunouts[i];
                    const currentRunout = newRunout.runoutDateTime;
                    let productReadings = this.getReadingsData(
                        newRunout.product_id
                    );

                    // Ensure `productReadings` exists or create one if it doesn't
                    if (!productReadings) {
                        productReadings = {
                            ProductID: newRunout.product_id,
                            volumeAtETA: null, // Set initial fields properly if needed
                            newRunout: null,
                            volumeAtClosestRunout: null
                        };
                        this.station.recentReadings.push(productReadings);
                    }

                    // Update the `newRunout` field in `productReadings`
                    productReadings.newRunout = newRunout;

                    // Update the closest runout
                    if (!closestRunout || currentRunout < closestRunout) {
                        closestRunout = currentRunout;
                        this.newClosestRunout = newRunout;
                    }
                }

                let volumesAtClosestRunoutPromises = [];

                // Prepare requests to fetch volumes at closest runout
                for (const productId in this.productOrderDetails) {
                    volumesAtClosestRunoutPromises.push(
                        apiService.getForecastedVolumeAtDateTime(
                            this.order.Station,
                            productId,
                            closestRunout
                        )
                    );
                }

                // Fetch all volumes at the closest runout
                const volumesAtClosestRunouts = await Promise.all(
                    volumesAtClosestRunoutPromises
                );

                // Update `volumeAtClosestRunout` for each product
                for (let i = 0; i < volumesAtClosestRunouts.length; i++) {
                    const volumeAtClosestRunout = volumesAtClosestRunouts[i];
                    let productReadings = this.getReadingsData(
                        volumeAtClosestRunout.product_id
                    );
                    console.info(
                        'Adding volume at closest runout for ' +
                            volumeAtClosestRunout.product_id
                    );
                    productReadings.volumeAtClosestRunout =
                        volumeAtClosestRunout.forecasted_volume;
                }
            } catch (error) {
                console.error(error);
            }
        },
        getUllage(productId, volume) {
            const stationProduct = this.getStationProduct(productId);
            const ullage =
                parseInt(stationProduct?.total_safe_fill) - parseInt(volume);
            return ullage;
        },
        getCompartments() {
            let compartments = [];
            for (let i = 1; i <= 6; i++) {
                let capacity = this.driverShift.trailer[`Comp${i}`];
                if (capacity) {
                    compartments.push({
                        compartment: i,
                        capacity: capacity,
                        usage: 0,
                        product: null
                    });
                }
            }
            return compartments;
        },

        // Function to auto-assign quantities
        autoAssignQuantities() {
            console.log('Starting auto assignment of quantities', this.station.recentReadings);

            // Step 1: Gather product data with ullage at ETA and runout datetime
            const productsData = this.station.recentReadings.map(reading => {
                const productId = reading.ProductID;
                const stationProduct = this.getStationProduct(productId);
                console.log('stationProduct', stationProduct);

                const safeFill = parseInt(stationProduct?.total_safe_fill);
                const volumeAtETA = parseInt(reading.volumeAtETA);
                const ullageAtETA = safeFill - volumeAtETA;

                const runoutAt = stationProduct.safe_runout_at;
                const runoutTimestamp = new Date(runoutAt).getTime();

                // Get product details
                const product = this.products.find(
                    p => p.ProductID === productId
                );
                const isLSD = product && product.MaxCap == 1;

                return {
                    productId,
                    ullageAtETA,
                    runoutAt,
                    runoutTimestamp,
                    productName: reading.Product,
                    isLSD
                };
            });

            // Step 2: Sort products by runout datetime ascending (closest to runout first)
            productsData.sort((a, b) => a.runoutTimestamp - b.runoutTimestamp);

            console.log('Sorted products by runout datetime:', productsData);

            const productA = productsData[0]; // Closest to runout
            const productB = productsData[1]; // Second closest to runout

            const compartments = this.getCompartments();

            const trailerLimit = this.limits[this.driverShift.trailer.NumAxles];
            let remainingTrailerCapacity = trailerLimit;

            console.log('Trailer capacity limit:', trailerLimit);

            const orderDetailsMap = {};

            // Step 3: Assign Product B (LSD) to the smallest compartment with adjusted quantity
            const smallestCompartment = compartments.sort(
                (a, b) => a.capacity - b.capacity
            )[0];

            // For LSD products, we need to calculate the unadjusted quantity that results in the correct adjusted amount for the compartment
            let compartmentCapacity = smallestCompartment.capacity;
            let quantityProductB = productB.isLSD
                ? Math.floor(compartmentCapacity / 1.14) // Adjust for LSD before assignment
                : Math.min(productB.ullageAtETA, compartmentCapacity);

            let adjustedQuantityProductB = productB.isLSD
                ? Math.ceil(quantityProductB * 1.14)
                : quantityProductB;

            // Ensure that the final adjusted quantity fits within the compartment's capacity
            adjustedQuantityProductB = Math.min(
                adjustedQuantityProductB,
                compartmentCapacity
            );

            remainingTrailerCapacity -= adjustedQuantityProductB;

            orderDetailsMap[productB.productId] = {
                OrderProdID: Date.now() + 1,
                OrderID: this.order.OrderID,
                ProductType: productB.productId,
                ProductQuantity: quantityProductB,
                AdjustedQuantity: adjustedQuantityProductB,
                DispatchedQuantity: null,
                DeliveredQuantity: null,
                ProductCode: null
            };

            console.log(
                `Assigned Product B (Product ID: ${productB.productId}) adjusted quantity to smallest compartment: ${adjustedQuantityProductB}`
            );

            // Step 4: Assign Product A to the remaining trailer capacity
            let remainingCompartments = compartments.filter(
                compartment => compartment !== smallestCompartment
            );
            let quantityProductA = Math.min(
                productA.ullageAtETA,
                remainingTrailerCapacity
            );
            let adjustedQuantityProductA = productA.isLSD
                ? Math.ceil(quantityProductA * 1.14)
                : quantityProductA;

            adjustedQuantityProductA = Math.min(
                adjustedQuantityProductA,
                remainingTrailerCapacity
            );
            remainingTrailerCapacity -= adjustedQuantityProductA;

            orderDetailsMap[productA.productId] = {
                OrderProdID: Date.now(),
                OrderID: this.order.OrderID,
                ProductType: productA.productId,
                ProductQuantity: quantityProductA,
                AdjustedQuantity: adjustedQuantityProductA,
                DispatchedQuantity: null,
                DeliveredQuantity: null,
                ProductCode: null
            };

            console.log(
                `Assigned Product A (Product ID: ${productA.productId}) adjusted quantity: ${adjustedQuantityProductA}`
            );

            // Step 5: If there is remaining capacity, add more of Product B to the other compartments
            if (
                remainingTrailerCapacity > 0 &&
                productB.ullageAtETA > quantityProductB
            ) {
                let additionalQuantityProductB = Math.min(
                    remainingTrailerCapacity,
                    productB.ullageAtETA - quantityProductB
                );

                adjustedQuantityProductB = productB.isLSD
                    ? Math.ceil(additionalQuantityProductB * 1.14)
                    : additionalQuantityProductB;

                remainingTrailerCapacity -= adjustedQuantityProductB;
                quantityProductB += additionalQuantityProductB;

                console.log(
                    `Increased Product B allocation by ${additionalQuantityProductB}.`
                );

                orderDetailsMap[
                    productB.productId
                ].ProductQuantity = quantityProductB;
                orderDetailsMap[
                    productB.productId
                ].AdjustedQuantity += adjustedQuantityProductB;
            }

            // Step 6: If there is still remaining capacity, add a third product
            if (remainingTrailerCapacity > 0 && productsData.length > 2) {
                const productC = productsData[2]; // Third closest to runout

                // For LSD products, we need to calculate the unadjusted quantity that results in the correct adjusted amount for the remaining capacity
                let quantityProductC = productC.isLSD
                    ? Math.min(
                          productC.ullageAtETA,
                          Math.floor(remainingTrailerCapacity / 1.14)
                      ) // Ensure it doesn't exceed the LSD adjusted capacity
                    : Math.min(productC.ullageAtETA, remainingTrailerCapacity);

                let adjustedQuantityProductC = productC.isLSD
                    ? Math.ceil(quantityProductC * 1.14) // Apply LSD adjustment factor
                    : quantityProductC;

                // Ensure that the adjusted quantity does not exceed the remaining trailer capacity
                adjustedQuantityProductC = Math.min(
                    adjustedQuantityProductC,
                    remainingTrailerCapacity
                );

                // Update the remaining trailer capacity after the assignment
                remainingTrailerCapacity -= adjustedQuantityProductC;

                // Store the assigned quantities for Product C
                orderDetailsMap[productC.productId] = {
                    OrderProdID: Date.now() + 2,
                    OrderID: this.order.OrderID,
                    ProductType: productC.productId,
                    ProductQuantity: quantityProductC, // Actual dispatched quantity
                    AdjustedQuantity: adjustedQuantityProductC, // Adjusted quantity for LSD
                    DispatchedQuantity: null,
                    DeliveredQuantity: null,
                    ProductCode: null
                };

                console.log(
                    `Assigned Product C (Product ID: ${productC.productId}) dispatched quantity: ${quantityProductC}, adjusted quantity: ${adjustedQuantityProductC}`
                );
            }

            // Update order details
            this.order.order_details = Object.values(orderDetailsMap);
            this.initializeProductOrderDetails();

            // Final recalculation of usage and trailer limit
            this.totalUsage = trailerLimit - remainingTrailerCapacity;
            this.trailerLimit = trailerLimit;

            this.getNewRunouts();
            console.info('Order details:', this.order.order_details);

            console.log('Final trailer usage:', this.totalUsage);
        },

        // Helper function to assign a product to compartments
        //IMPORTANT: UNUSED FOR NOW
        assignProductToCompartments(productId, quantityToAssign, compartments) {
            let assignedQuantity = 0;
            let updatedCompartments = compartments.map(compartment => ({
                ...compartment
            }));

            // Sort compartments by capacity descending to fill larger ones first
            const compartmentsByCapacityDesc = [...updatedCompartments].sort(
                (a, b) => b.capacity - a.capacity
            );

            let quantityRemaining = quantityToAssign;

            for (let compartment of compartmentsByCapacityDesc) {
                if (
                    (compartment.product === null ||
                        compartment.product === productId) &&
                    quantityRemaining > 0
                ) {
                    const availableCapacity =
                        compartment.capacity - (compartment.usage || 0);
                    const quantityToLoad = Math.min(
                        quantityRemaining,
                        availableCapacity
                    );
                    if (quantityToLoad > 0) {
                        compartment.usage =
                            (compartment.usage || 0) + quantityToLoad;
                        compartment.product = productId;
                        assignedQuantity += quantityToLoad;
                        quantityRemaining -= quantityToLoad;
                    }
                }
            }

            return {
                assignedQuantity,
                updatedCompartments
            };
        }
    }
};
</script>

<style scoped></style>
